import React, { lazy, StrictMode, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SignUpPageHeader } from '@mobi/account/Areas/SignUp/Components'
import type { Campaign } from '@mobi/account/Areas/SignUp'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { MainContainer } from '@core/Components/Containers'
import { useFeature, useObservableImmutable } from '@core/Utils/hooks'
import { trackOptimoveEvent } from '@core/Services/Optimove/optimove'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { attemptLogin, showLogin } from '@core/Areas/Login/helpers'
import { openDeposit } from '@core/Areas/Deposit'
import { useDisableAppHeaderRender } from '@core/Areas/AppHeader/hooks/useDisableAppHeaderRender'
import { getIsReloadPending } from '@core/Areas/KambiClient/Store/selectors'
import { setIsInSignUp, setIsReloadPending } from '@core/Areas/KambiClient/Store'
import { SignUpVerificationPage } from '@core/Areas/Settings/Components/MFASettings/Pages/SignUpVerificationPage'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { setTrustedDeviceDetails } from '@core/Areas/Login/Store'

const SignUpPage = lazy(() => import('@mobi/account/Areas/SignUp/Components/SignUpPage'))

// needs to be memoed, because it sends the verification code on mount, and
// we don't want this to be spammed
const SignupVerificationPageMemo = React.memo(SignUpVerificationPage)

export const SignUp = () => {
  const dispatch = useDispatch()
  const isReloadPending = useSelector(getIsReloadPending)
  useDisableAppHeaderRender()
  const { isLoggedIn } = useObservableImmutable(userAccountState$, ['isLoggedIn'])
  const availableCampaigns = useObservableImmutable(userAccountState$, ['campaigns']).campaigns
  const firstLiveCampaign: Campaign | undefined = availableCampaigns?.find(x => x.isLive)

  const signUpAttemptLogin = async (request: Parameters<typeof attemptLogin>[0]) => {
    const result = await attemptLogin(request)

    if (result.status === 'SUCCESSFUL') {
      if (result.newDeviceKey && result.newDeviceGroupKey) {
        dispatch(
          setTrustedDeviceDetails({
            accountIdentifier: request.accountIdentifier,
            deviceDetails: {
              deviceKey: result.newDeviceKey,
              deviceGroupKey: result.newDeviceGroupKey,
              deviceSecret: request.deviceSecret,
              isRemembered: false,
            },
          })
        )
      }
    }

    return result
  }

  const mfaInSignup = useFeature(FeatureFlags.MFA_IN_SIGNUP.key) as
    | 'off'
    | 'verify'
    | 'verify_and_enable'

  return (
    <StrictMode>
      <Suspense fallback={<Spinner key='signup-loader' />}>
        <SignUpPageHeader />
        <MainContainer data-tid-sign-up background='white'>
          <SignUpPage
            isLoggedIn={isLoggedIn}
            attemptLogin={signUpAttemptLogin}
            showLogin={showLogin}
            campaign={firstLiveCampaign}
            openDeposit={openDeposit}
            trackOptimoveEvent={trackOptimoveEvent}
            onMount={() => {
              dispatch(setIsInSignUp(true))
            }}
            onDispose={() => {
              dispatch(setIsInSignUp(false))
              if (isReloadPending) {
                dispatch(setIsReloadPending(false))
                window.location.reload()
              }
            }}
            mfaInSignupFeatureFlag={mfaInSignup}
            MfaVerificationPage={SignupVerificationPageMemo}
          />
        </MainContainer>
      </Suspense>
    </StrictMode>
  )
}
